import React, { FC } from 'react';
import { useTheme } from 'styled-components';
import * as S from '@/components/Ad/MainInfo/MainInfo.styles';
import { formatNumber } from '@/utils/helpers';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useServerDataContext } from '@/contexts';
import { useResponsive } from '@/hooks/useResponsive';
import { HeartIcon } from '@/components/UI/Icons/Icons';
import { useIsShop } from '@/hooks/useIsShop';

interface Props {
  canBookmark?: boolean;
  isBookmarked?: boolean;
  onBookmarkClick?: () => void;
}

export const MainInfo: FC<Props> = ({
  canBookmark,
  isBookmarked,
  onBookmarkClick,
}) => {
  const { initialAdDetails } = useServerDataContext();
  const { adDetails } = useAdDetails();
  const { isDesktop } = useResponsive();
  const adData = adDetails ?? initialAdDetails;
  const isShop = useIsShop(adData);
  const theme = useTheme();
  const { price, title } = adData ?? {};

  function getPrice() {
    if (price === undefined) {
      return (
        <SkeletonLoader
          dataTestId="ad-price-skeleton-loader"
          borderRadius={14}
          width="170px"
          height="28.8px"
        />
      );
    }

    if (price === null || price === 0) {
      return null;
    }

    return (
      <S.Price data-testid={AdPageTestIds.price}>
        {formatNumber(price, 2, true)} ₼
      </S.Price>
    );
  }

  return (
    <S.MainInfoBox>
      {!isDesktop && getPrice()}
      {title ? (
        <S.Title as="h1" data-testid={AdPageTestIds.title}>
          {title}
        </S.Title>
      ) : (
        <SkeletonLoader
          dataTestId="ad-title-skeleton-loader"
          borderRadius={14}
          height={isDesktop ? 24 : 22}
          width={isDesktop ? 400 : '100%'}
        />
      )}
      {canBookmark && (
        <S.BookmarkButton
          appearance="transparent"
          aria-label="Toggle bookmark"
          onClick={onBookmarkClick}
          data-stat={
            !isBookmarked && isShop ? 'shop-ad-bookmarking' : undefined
          }
          className={!isBookmarked ? 'add_bookmark' : undefined}
        >
          <HeartIcon
            data-testid={AdPageTestIds.headerActions.bookmarkIcon}
            fill={isBookmarked ? theme.palette.brand.main : 'none'}
            stroke={
              isBookmarked
                ? theme.palette.brand.main
                : theme.palette.primary.labels
            }
          />
        </S.BookmarkButton>
      )}
    </S.MainInfoBox>
  );
};
